import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { isThemeFlagOn } from "../gameSetupStorage";
import {
  ensureThemeJsonArray,
  getDvSourceImageUrl,
  getLandingPageTitleColor,
  themeBackgroundKeyForViewport,
  toCssUrlValue,
} from "../../functions/themeAssets";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  ensureStageRowId,
  fetchGameStageRecord,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  saveGameStageAndReport,
  saveSnakesLaddersAnswerProgress,
  setStageActive,
} from "../../functions/stageReportSync";
import {
  MAX_BUCKETS,
  getDragDropQuestions,
  normalizeCategoryName,
  shuffleCopy,
} from "./dragDropUtils";
import "./dragdrop.css";

const STAGE_NUM = 1;
const DEMO_PLANS_URL = "https://extramileplay.com/plans";
const CORRECT_POPUP_MS = 1200;
const WRONG_POPUP_MS = 2200;

function getStoredUser(authUser) {
  if (authUser && typeof authUser === "object") return authUser;
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    return {};
  }
}

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})/i.exec(String(hex || "").trim());
  if (!m) return [0, 0, 0];
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

/** Pastel halo/ring shade of a bucket color, for the count badge + shape border. */
function lightenHex(hex, amount) {
  const [r, g, b] = hexToRgb(hex);
  const mix = (c) => Math.round(c + (255 - c) * amount);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

function formatClock(totalSec) {
  const sec = Math.max(0, Math.floor(Number(totalSec) || 0));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function escapeHtml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function correctFeedbackHtml(pointsThisAnswer) {
  return `<div class="dd-feedback dd-feedback--correct">
    <div class="dd-feedback__icon-wrap" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M5 13l4 4L19 7" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </div>
    <h3 class="dd-feedback__title">Correct!</h3>
    <span class="dd-feedback__points">+${escapeHtml(pointsThisAnswer)} points</span>
  </div>`;
}

function wrongFeedbackHtml(correctAnswerLabel) {
  return `<div class="dd-feedback dd-feedback--wrong">
    <div class="dd-feedback__icon-wrap" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M6 6l12 12M18 6L6 18" stroke="#fff" stroke-width="3" stroke-linecap="round" />
      </svg>
    </div>
    <h3 class="dd-feedback__title">Oops, wrong answer!</h3>
    <p class="dd-feedback__text">This statement signifies <strong>${escapeHtml(correctAnswerLabel)}</strong>.</p>
  </div>`;
}

export default function Game() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);
  const adminToken = useSelector(selectAdminToken);

  const storedUser = useMemo(() => getStoredUser(user), [user]);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);

  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const allQuestions = useMemo(() => getDragDropQuestions(themeData), [themeData]);
  const categories = useMemo(() => ensureThemeJsonArray(themeData?.categories), [themeData]);
  const categoryByName = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => {
      const name = normalizeCategoryName(c?.name);
      if (name) map.set(name, c);
    });
    return map;
  }, [categories]);
  const shuffleEnabled = isThemeFlagOn(themeData?.shuffle, false);
  const pointsPerCorrect = Math.max(
    1,
    parseInt(themeData?.correctPoints ?? themeData?.points, 10) || 1
  );
  const configuredCount = parseInt(themeData?.noOfQuestion, 10);
  const targetCount =
    Number.isFinite(configuredCount) && configuredCount > 0
      ? Math.min(configuredCount, allQuestions.length)
      : allQuestions.length;

  const titleColor = String(themeData?.title_color || "#000000").trim();
  const boxColor = String(themeData?.questionBox_color || "#fbf99f").trim();
  const bucketLabelColor = getLandingPageTitleColor(themeData) || "#fff";
  const heading = String(themeData?.landing_page_title || "").trim();
  // Fallback palette for buckets whose label has no matching category (legacy data only —
  // each category normally carries its own color now).
  const fallbackBucketColors = useMemo(() => {
    const base = [
      String(themeData?.main_color1 || "#ff0000").trim(),
      String(themeData?.main_color2 || "#0000ff").trim(),
      String(themeData?.main_color3 || "#008000").trim(),
    ];
    return Array.from({ length: MAX_BUCKETS }, (_, i) => base[i % base.length]);
  }, [themeData]);

  const backgroundUrl = useMemo(() => {
    const key = themeBackgroundKeyForViewport(themeData);
    return key ? toCssUrlValue(getDvSourceImageUrl(key)) : "none";
  }, [themeData]);

  const [questions, setQuestions] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [bucketCounts, setBucketCounts] = useState(() => Array(MAX_BUCKETS).fill(0));
  const [elapsedSec, setElapsedSec] = useState(0);
  const [timerOn, setTimerOn] = useState(false);
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [hoverBucket, setHoverBucket] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [dbReady, setDbReady] = useState(false);
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || ""
  );

  const qIndexRef = useRef(0);
  const scoreRef = useRef(0);
  const elapsedRef = useRef(0);
  const bucketCountsRef = useRef(Array(MAX_BUCKETS).fill(0));
  const answersRef = useRef([]);
  const eqCounterRef = useRef(0);
  const finished = useRef(false);
  const startedRef = useRef(false);
  const dragState = useRef({ dragging: false, startX: 0, startY: 0 });
  const cardRef = useRef(null);
  const bucketRef0 = useRef(null);
  const bucketRef1 = useRef(null);
  const bucketRef2 = useRef(null);
  const bucketRef3 = useRef(null);
  const bucketRef4 = useRef(null);
  const bucketRef5 = useRef(null);
  const bucketRefs = useMemo(
    () => [bucketRef0, bucketRef1, bucketRef2, bucketRef3, bucketRef4, bucketRef5],
    [bucketRef0, bucketRef1, bucketRef2, bucketRef3, bucketRef4, bucketRef5]
  );

  useEffect(() => {
    dispatch(setBackButtonUrl("/rules"));
  }, [dispatch]);

  // Unlock document scroll for the stage (login/rules CSS locks body overflow on mobile portrait).
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const root = document.getElementById("root");
    body.classList.add("dd-stage-open");
    html.classList.add("dd-stage-open");

    const apply = () => {
      html.style.setProperty("overflow-x", "hidden", "important");
      html.style.setProperty("overflow-y", "auto", "important");
      html.style.setProperty("height", "auto", "important");
      html.style.setProperty("max-height", "none", "important");
      body.style.setProperty("overflow", "visible", "important");
      body.style.setProperty("height", "auto", "important");
      body.style.setProperty("max-height", "none", "important");
      if (root) {
        root.style.setProperty("overflow", "visible", "important");
        root.style.setProperty("height", "auto", "important");
      }
    };

    apply();
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);

    return () => {
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
      body.classList.remove("dd-stage-open");
      html.classList.remove("dd-stage-open");
      ["overflow", "overflow-x", "overflow-y", "height", "max-height"].forEach((prop) => {
        html.style.removeProperty(prop);
        body.style.removeProperty(prop);
        root?.style.removeProperty(prop);
      });
    };
  }, []);

  // Build the working question list once the theme's questions are known.
  useEffect(() => {
    if (!allQuestions.length) {
      setQuestions([]);
      return;
    }
    const source = shuffleEnabled ? shuffleCopy(allQuestions) : allQuestions;
    setQuestions(source.slice(0, targetCount || source.length));
  }, [allQuestions, shuffleEnabled, targetCount]);

  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      setStageActive(STAGE_NUM, storedUser);
      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }
      try {
        const rowId = await ensureStageRowId({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          onStageRowId: (id) => {
            sessionStorage.setItem(STAGE_ROW_KEY, String(id));
            if (!cancelled) setStageRowId(String(id));
          },
        });
        const st = await fetchGameStageRecord({
          backendBase,
          adminToken,
          storedUser,
          stageId: rowId || sessionStorage.getItem(STAGE_ROW_KEY),
        });
        if (st && typeof st === "object") {
          hydrateCompletedStagesFromRecord(st);
          const redirect = getRedirectForPlayedStage(st, STAGE_NUM, {});
          if (redirect && redirect !== "/stage1" && redirect !== "/game") {
            if (!cancelled) navigate(redirect, { replace: true });
            return;
          }
        }
      } catch (err) {
        console.error("Game bootstrap:", err);
      } finally {
        if (!cancelled) setDbReady(true);
      }
    }
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [adminToken, backendBase, isDemoBypass, navigate, storedUser]);

  // No questions configured at all — match old draganddrop.php's guard.
  useEffect(() => {
    if (themeStatus === "loading" || !dbReady) return;
    if (allQuestions.length > 0) return;
    Swal.fire({
      icon: "warning",
      title: "No Questions Found",
      text: "Please add questions from Admin before continuing.",
      confirmButtonText: "OK",
    }).then(() => navigate("/rules", { replace: true }));
  }, [allQuestions.length, themeStatus, dbReady, navigate]);

  useEffect(() => {
    if (startedRef.current || !dbReady || !questions.length) return;
    startedRef.current = true;
    setTimerOn(true);
  }, [dbReady, questions.length]);

  useEffect(() => {
    if (!timerOn) return undefined;
    const id = setInterval(() => {
      elapsedRef.current += 1;
      setElapsedSec(elapsedRef.current);
    }, 1000);
    return () => clearInterval(id);
  }, [timerOn]);

  const currentQuestion = questions[qIndex] || null;

  // Buckets always show every configured category, regardless of how many option_*
  // columns a CSV-imported question row happened to fill in. Legacy themes without
  // any configured categories fall back to the question's own option labels.
  const bucketLabels = useMemo(() => {
    if (categories.length > 0) return categories.map((c) => String(c?.name ?? "").trim());
    return currentQuestion ? currentQuestion.options : [];
  }, [categories, currentQuestion]);

  const persistAndLeave = useCallback(
    async (finalScore) => {
      const finalTime = formatClock(elapsedRef.current);
      setTimerOn(false);
      try {
        if (!isDemoBypass && backendBase && adminToken) {
          const saveResult = await saveGameStageAndReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            stageId: stageRowId || sessionStorage.getItem(STAGE_ROW_KEY),
            stageNumber: STAGE_NUM,
            score: finalScore,
            total_score: finalScore,
            time: finalTime,
            gameover: true,
            user_answers: answersRef.current,
          });
          if (saveResult?.stageId) {
            sessionStorage.setItem(STAGE_ROW_KEY, String(saveResult.stageId));
            setStageRowId(String(saveResult.stageId));
          }
        }
      } catch (err) {
        console.error("Game finish:", err);
      }
      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);
      if (isDemoBypass) {
        window.location.href = DEMO_PLANS_URL;
        return;
      }
      navigate("/thankyou", {
        replace: true,
        state: { points: finalScore, time: finalTime },
      });
    },
    [adminToken, backendBase, isDemoBypass, navigate, stageRowId, storedUser]
  );

  const saveProgressAfterAnswer = useCallback(
    async (pointsThisAnswer) => {
      if (isDemoBypass || !backendBase || !adminToken) return;
      const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      if (!stageId) return;
      try {
        await saveSnakesLaddersAnswerProgress({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          answerIndex: Math.max(1, Math.min(eqCounterRef.current, 13)),
          pointsThisAnswer,
          totalScore: scoreRef.current,
          time: formatClock(elapsedRef.current),
          userAnswers: answersRef.current,
        });
      } catch (err) {
        console.error("saveProgressAfterAnswer:", err);
      }
    },
    [adminToken, backendBase, isDemoBypass, stageRowId, storedUser]
  );

  const advance = useCallback(() => {
    const next = qIndexRef.current + 1;
    if (next >= questions.length) {
      finished.current = true;
      persistAndLeave(scoreRef.current);
      return;
    }
    qIndexRef.current = next;
    setQIndex(next);
    setBusy(false);
  }, [questions.length, persistAndLeave]);

  const handleDrop = useCallback(
    async (bucketIndex) => {
      if (!currentQuestion || busy || finished.current) return;
      setBusy(true);

      const droppedLabel = bucketLabels[bucketIndex];
      const correct =
        normalizeCategoryName(droppedLabel) === normalizeCategoryName(currentQuestion.correctAnswer);
      const nextCounts = bucketCountsRef.current.map((c, i) =>
        i === bucketIndex ? c + 1 : c
      );
      bucketCountsRef.current = nextCounts;
      setBucketCounts(nextCounts);

      eqCounterRef.current += 1;
      const isFirstQuestion = eqCounterRef.current === 1;
      answersRef.current.push({
        questionId: currentQuestion.id,
        questionText: currentQuestion.question_name,
        userAnswer: droppedLabel,
        correctAnswer: currentQuestion.correctAnswer,
        isCorrect: correct,
        questionNumber: eqCounterRef.current,
        answeredAt: new Date().toISOString(),
      });

      const pointsThisAnswer = correct ? pointsPerCorrect : 0;
      if (correct) {
        scoreRef.current += pointsPerCorrect;
        setScore(scoreRef.current);
      }

      await Swal.fire(
        correct
          ? {
              html: correctFeedbackHtml(pointsThisAnswer),
              timer: CORRECT_POPUP_MS,
              showConfirmButton: false,
              showClass: { popup: "dd-feedback-popup--in" },
              customClass: { popup: "dd-feedback-popup" },
            }
          : {
              html: wrongFeedbackHtml(currentQuestion.correctAnswer),
              timer: WRONG_POPUP_MS,
              showConfirmButton: false,
              showClass: { popup: "dd-feedback-popup--in" },
              customClass: { popup: "dd-feedback-popup" },
            }
      );

      // Demo sessions only get a taste of the first question, then a CTA to subscribe.
      if (isDemoBypass && isFirstQuestion) {
        finished.current = true;
        await Swal.fire({
          icon: "info",
          text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
          confirmButtonText: "OK",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
        window.location.assign(DEMO_PLANS_URL);
        return;
      }

      advance();
      saveProgressAfterAnswer(pointsThisAnswer);
    },
    [currentQuestion, busy, bucketLabels, isDemoBypass, pointsPerCorrect, saveProgressAfterAnswer, advance]
  );

  const bucketIndexAtPoint = useCallback(
    (x, y) =>
      bucketRefs.findIndex((ref) => {
        const el = ref.current;
        if (!el) return false;
        const r = el.getBoundingClientRect();
        return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
      }),
    [bucketRefs]
  );

  const handlePointerDown = (e) => {
    if (busy || !currentQuestion) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragState.current = { dragging: true, startX: e.clientX, startY: e.clientY };
    setIsDragging(true);
  };

  const handlePointerMove = (e) => {
    if (!dragState.current.dragging) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    setDragPos({ x: dx, y: dy });
    setHoverBucket(bucketIndexAtPoint(e.clientX, e.clientY));
  };

  const handlePointerUp = (e) => {
    if (!dragState.current.dragging) return;
    dragState.current.dragging = false;
    setIsDragging(false);
    const targetIndex = bucketIndexAtPoint(e.clientX, e.clientY);
    setDragPos({ x: 0, y: 0 });
    setHoverBucket(-1);
    if (targetIndex >= 0) handleDrop(targetIndex);
  };

  const stillPreparing =
    allQuestions.length > 0 && questions.length === 0 && !finished.current;

  if ((themeStatus === "loading" && !themeData) || !dbReady || stillPreparing) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  const pointsLabel = String(Math.max(0, Math.floor(score))).padStart(2, "0");

  return (
    <div className="dd-page" style={{ backgroundImage: backgroundUrl }}>
      <div className="dd-hud-bar">
        <div className="dd-hud">
          <div
            className="dd-hud-pill dd-hud-pill--timer"
            aria-label={`Time ${formatClock(elapsedSec)}`}
          >
            <span className="dd-hud-icon" aria-hidden>
              <svg viewBox="0 0 40 44" width="36" height="40">
                <rect x="8" y="2" width="24" height="5" rx="1.5" fill="#3a3a3a" />
                <rect x="8" y="37" width="24" height="5" rx="1.5" fill="#3a3a3a" />
                <path
                  d="M11 7h18l-7.5 13.5L29 34H11l7.5-13.5L11 7z"
                  fill="#fff"
                  stroke="#3a3a3a"
                  strokeWidth="1.4"
                  strokeLinejoin="round"
                />
                <path d="M14 9.5h12l-4.2 7.6H18.2L14 9.5z" fill="url(#ddHourSandTop)" />
                <path d="M18.2 25.8h3.6L26 32.5H14l4.2-6.7z" fill="url(#ddHourSandBot)" />
                <circle cx="20" cy="21" r="1.1" fill="#f5c842" />
                <circle cx="18.6" cy="23.2" r="0.8" fill="#f0a020" />
                <circle cx="21.4" cy="23.8" r="0.7" fill="#f5c842" />
                <defs>
                  <linearGradient id="ddHourSandTop" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ffe27a" />
                    <stop offset="100%" stopColor="#f0a020" />
                  </linearGradient>
                  <linearGradient id="ddHourSandBot" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f5c842" />
                    <stop offset="100%" stopColor="#e08a10" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
            <span className="dd-hud-value">{formatClock(elapsedSec)}</span>
          </div>
        </div>

        <div className="dd-hud">
          <div className="dd-hud-pill dd-hud-pill--points" aria-label={`Points ${pointsLabel}`}>
            <span className="dd-hud-icon" aria-hidden>
              <svg viewBox="0 0 48 48" width="40" height="40">
                <path
                  d="M24 4.5l5.4 11 12.1 1.8-8.8 8.5 2.1 12.1L24 32.4 13.2 37.9l2.1-12.1-8.8-8.5 12.1-1.8L24 4.5z"
                  fill="#c47810"
                  transform="translate(0 2.5)"
                />
                <path
                  d="M24 4.5l5.4 11 12.1 1.8-8.8 8.5 2.1 12.1L24 32.4 13.2 37.9l2.1-12.1-8.8-8.5 12.1-1.8L24 4.5z"
                  fill="url(#ddStarFill)"
                  stroke="#e8a010"
                  strokeWidth="0.8"
                />
                <defs>
                  <linearGradient id="ddStarFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fff3a0" />
                    <stop offset="45%" stopColor="#ffd54a" />
                    <stop offset="100%" stopColor="#f0a020" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
            <span className="dd-hud-value">{pointsLabel}</span>
          </div>
        </div>
      </div>

      <h3 className="dd-title" style={{ color: titleColor }}>
        {heading}
      </h3>

      <div className="dd-buckets-row">
        {bucketLabels.map((label, i) => {
          const category = categoryByName.get(normalizeCategoryName(label));
          const bucketColor = category?.color || fallbackBucketColors[i];
          const halo = lightenHex(bucketColor, 0.45);
          const categoryImageUrl = category?.image ? getDvSourceImageUrl(category.image) : "";
          const hasImage = Boolean(categoryImageUrl);
          return (
            <div
              key={i}
              ref={bucketRefs[i]}
              className={`dd-bucket${hoverBucket === i ? " is-drop-target" : ""}${hasImage ? " has-image" : ""}`}
            >
              <div
                className="dd-bucket-shape"
                style={hasImage ? undefined : { background: bucketColor, borderColor: halo }}
              >
                {hasImage ? (
                  <span className="dd-bucket-image-wrap">
                    <img src={categoryImageUrl} alt="" className="dd-bucket-image" />
                    <span className="dd-bucket-image-count" style={{ color: bucketLabelColor }}>
                      {bucketCounts[i]}
                    </span>
                  </span>
                ) : (
                  <span className="dd-bucket-label" style={{ color: bucketLabelColor }}>
                    {label}
                  </span>
                )}
              </div>
              {!hasImage ? (
                <div
                  className="dd-bucket-count"
                  style={{ background: bucketColor, borderColor: halo, color: bucketLabelColor }}
                >
                  {bucketCounts[i]}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="dd-source-wrap">
        {currentQuestion ? (
          <div key={currentQuestion.id} className="dd-question-enter">
            <div
              ref={cardRef}
              className={`dd-draggable${isDragging ? " is-dragging" : ""}`}
              style={{
                transform: `translate(${dragPos.x}px, ${dragPos.y}px)`,
              }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            >
              <span className="dd-draggable-inner" style={{ background: boxColor }}>
                {currentQuestion.question_name}
              </span>
            </div>
          </div>
        ) : null}
      </div>

      <div className="dd-rotate-overlay" aria-hidden>
        Please rotate your device for the best experience
      </div>
    </div>
  );
}
